import apiClient from '../config/api';
import { Facture, AvoirRequestDTO } from '../types';

/**
 * Service pour la gestion des avoirs (credit notes)
 */
class AvoirService {
  /**
   * Créer un avoir pour une facture
   * @param data - Données de l'avoir à créer
   * @returns L'avoir créé (en tant que Facture avec type AVOIR)
   */
  async creerAvoir(data: AvoirRequestDTO): Promise<Facture> {
    console.log('📝 Création d\'un avoir:', data);
    try {
      const response = await apiClient.post<Facture>('/api/factures/avoir', data);
      console.log('✅ Avoir créé avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la création de l\'avoir:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les avoirs d'une facture
   * @param factureId - ID de la facture originale
   * @returns Liste des avoirs liés à la facture
   */
  async getAvoirsByFactureId(factureId: number): Promise<Facture[]> {
    console.log('📄 Récupération des avoirs pour la facture ID:', factureId);
    try {
      const response = await apiClient.get<Facture[]>(`/api/factures/${factureId}/avoirs`);
      console.log('✅ Avoirs récupérés:', response.data.length, 'avoir(s)');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des avoirs:', error);
      throw error;
    }
  }

  /**
   * Télécharger le PDF d'un avoir
   * @param id - ID de l'avoir
   * @returns Le PDF en tant que Blob
   */
  async downloadPDF(id: number): Promise<Blob> {
    console.log('📄 Téléchargement du PDF de l\'avoir ID:', id);
    try {
      const response = await apiClient.get(`/api/factures/${id}/pdf`, {
        responseType: 'blob',
      });
      console.log('✅ PDF de l\'avoir téléchargé, taille:', response.data.size, 'bytes');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement du PDF de l\'avoir:', error);
      throw error;
    }
  }

  /**
   * Obtenir le PDF d'un avoir en base64
   * @param id - ID de l'avoir
   * @returns Le PDF encodé en base64
   */
  async getPDFBase64(id: number): Promise<string> {
    console.log('📄 Récupération du PDF en base64 de l\'avoir ID:', id);
    try {
      const blob = await this.downloadPDF(id);

      // Convertir le Blob en base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          // Extraire la partie base64 (après "data:application/pdf;base64,")
          const base64Data = base64.split(',')[1];
          console.log('✅ PDF de l\'avoir converti en base64, taille:', base64Data.length, 'caractères');
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      console.error('❌ Erreur lors de la conversion du PDF de l\'avoir en base64:', error);
      throw error;
    }
  }
}

export default new AvoirService();
